import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { SalaryOvertimeTier } from '@/services/compensation/types';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';

interface OvertimeTierBuilderProps {
  tiers: SalaryOvertimeTier[];
  onChange: (tiers: SalaryOvertimeTier[]) => void;
  description?: string;
  baseRate?: number;
}

export function OvertimeTierBuilder({ tiers, onChange, description, baseRate }: OvertimeTierBuilderProps) {
  const { currencyCode } = useWorkplaceCurrency();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };
  const [showPreview, setShowPreview] = useState(false);

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    const newFromHours = lastTier ? (lastTier.to_hours ?? 0) : 0;
    
    const newTier: SalaryOvertimeTier = {
      from_hours: newFromHours,
      to_hours: null, // Unlimited by default
      multiplier: 1.5
    };
    onChange([...tiers, newTier]);
  };

  const removeTier = (index: number) => {
    onChange(tiers.filter((_, i) => i !== index));
  };

  const updateTier = (index: number, field: keyof SalaryOvertimeTier, value: string | number) => {
    const updated = [...tiers];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const isValid = () => {
    return tiers.every(tier => 
      tier.from_hours >= 0 && 
      tier.multiplier > 0 &&
      (tier.to_hours === null || tier.to_hours > tier.from_hours)
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Overtime Tiers</CardTitle>
        {description && (
          <CardDescription className="text-sm">{description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {tiers.length === 0 && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              No overtime tiers configured. Add tiers to define how overtime is calculated on non-scheduled days.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          {tiers.map((tier, index) => (
            <div key={index} className="flex items-start gap-3 p-3 border rounded-lg bg-card">
              <div className="flex-1 grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label htmlFor={`tier-from-${index}`} className="text-xs">
                    From Hours
                  </Label>
                  <Input
                    id={`tier-from-${index}`}
                    type="number"
                    step="0.5"
                    min="0"
                    value={tier.from_hours}
                    onChange={(e) => updateTier(index, 'from_hours', parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="h-9"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`tier-to-${index}`} className="text-xs">
                    To Hours
                  </Label>
                  <Input
                    id={`tier-to-${index}`}
                    type="number"
                    step="0.5"
                    min="0"
                    value={tier.to_hours ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateTier(index, 'to_hours', val === '' ? null : parseFloat(val) || 0);
                    }}
                    placeholder="Unlimited"
                    className="h-9"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`tier-multiplier-${index}`} className="text-xs">
                    Multiplier
                  </Label>
                  <Input
                    id={`tier-multiplier-${index}`}
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={tier.multiplier}
                    onChange={(e) => updateTier(index, 'multiplier', parseFloat(e.target.value) || 0)}
                    placeholder="1.5"
                    className="h-9"
                  />
                </div>
                
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeTier(index)}
                    className="h-9 w-9"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addTier}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Tier
          </Button>

          {baseRate && baseRate > 0 && tiers.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? 'Hide' : 'Show'} Preview
            </Button>
          )}
        </div>

        {showPreview && baseRate && baseRate > 0 && tiers.length > 0 && (
          <Alert className="mt-4">
            <Info className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium text-sm">Example calculation (8 hours worked on non-scheduled day):</p>
                <div className="text-xs space-y-1 pl-4">
                  {(() => {
                    let remainingHours = 8;
                    let totalAmount = 0;
                    const sortedTiers = [...tiers].sort((a, b) => a.from_hours - b.from_hours);
                    
                    return sortedTiers.map((tier, idx) => {
                      if (remainingHours <= 0) return null;
                      
                      const hoursInThisTier = tier.to_hours 
                        ? Math.min(remainingHours, tier.to_hours - tier.from_hours)
                        : remainingHours;
                      
                      const rate = baseRate * tier.multiplier;
                      const amount = hoursInThisTier * rate;
                      totalAmount += amount;
                      remainingHours -= hoursInThisTier;
                      
                      return (
                        <div key={idx} className="flex justify-between">
                          <span>{tier.from_hours}h - {tier.to_hours ? `${tier.to_hours}h` : '∞'}:</span>
                          <span>{hoursInThisTier.toFixed(1)} hrs × {formatCurrency(rate)}/hr = {formatCurrency(amount)}</span>
                        </div>
                      );
                    });
                  })()}
                  <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                    <span>Total:</span>
                    <span>{formatCurrency((() => {
                      let total = 0;
                      let remaining = 8;
                      const sortedTiers = [...tiers].sort((a, b) => a.from_hours - b.from_hours);
                      sortedTiers.forEach(tier => {
                        if (remaining <= 0) return;
                        const hoursInTier = tier.to_hours 
                          ? Math.min(remaining, tier.to_hours - tier.from_hours)
                          : remaining;
                        total += hoursInTier * baseRate * tier.multiplier;
                        remaining -= hoursInTier;
                      });
                      return total;
                    })())}</span>
                  </div>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {!isValid() && tiers.length > 0 && (
          <Alert variant="destructive">
            <AlertDescription className="text-sm">
              All tiers must have valid hours (from ≥ 0, to &gt; from or unlimited) and multiplier &gt; 0.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
